import { NO_SERVICE_TYPE } from '@servwell/shared';
import { pickedFrom } from './ServiceTypePicker';

/** A type as the filter offers it: every live one, turned-off ones included. */
type FilterType = { id: string; label: string; active: boolean };

/**
 * What the Jobs list is filtered by: `value` while the shop's list still has that type, and
 * "all" ('') once it has gone (deleted since it was picked, or since the link naming it was
 * made). Derived, so the control and the query can never disagree: a filter the dropdown cannot
 * show would draw "All" over a list narrowed to nothing. "No service type" and "all" never go.
 * While the list has not loaded, `value` stands: it may have come from the address
 * (`/jobs?serviceType=`, ticket 06), and nothing can say yet whether its type is still there.
 * A type missing from the list the page holds is also taken out of the address, once the
 * server's own answer to a fresh ask confirms it has gone (`deadFilterWatch`). A type is on offer here while it is live, turned off or not: the
 * picker's rule (`pickedFrom`) over every type.
 */
export function shownServiceTypeFilter(
  types: readonly FilterType[] | undefined,
  value: string,
): string {
  if (value === '' || value === NO_SERVICE_TYPE || !types) return value;
  return pickedFrom(types, value);
}

/**
 * Whether the filter names a type missing from the list the page holds: 'confirm', ask the
 * server before believing it; else 'keep'. Every job, "No service type" and a type on the list
 * are kept, and so is anything while there is no list yet. Missing is not gone: the list may be
 * the saved cache, another screen's inside the stale time, or a fetch from before a type made
 * seconds ago in another tab (whoever sent the link just made it).
 */
export function deadFilterStep(
  types: readonly FilterType[] | undefined,
  value: string,
): 'keep' | 'confirm' {
  return types && shownServiceTypeFilter(types, value) !== value ? 'confirm' : 'keep';
}

/** A list read's outcome, as a query's `refetch()` answers it. */
type TypesRead = { status: string; data: readonly FilterType[] | undefined };

/**
 * The Jobs list's dead-filter check, kept apart from React so a test can drive it. `see` is
 * told each list and filter the page draws; for a filter missing from that list, it asks the
 * list again (`refetch`) and takes the id out of the address (`clear`, the replacing setter)
 * ONLY from that ask's own answer, and only if all of these still hold when it lands:
 *   - the ask succeeded (a failed one keeps its old data, which proves nothing);
 *   - its list lacks the id;
 *   - the page is still there (`start` / `stop`, the mount's effect);
 *   - the filter is still that id (the person may have moved on).
 * Anything else keeps the address. It asks once per id while the page is mounted, so a failed
 * ask, or re-renders, never loop; after a clear the filter is every job, which asks nothing.
 */
export function deadFilterWatch() {
  const asked = new Set<string>();
  let alive = false;
  let current = '';
  return {
    start() {
      alive = true;
    },
    stop() {
      alive = false;
    },
    /** A render's list and filter. Answers the ask it made, if any, for a test to await. */
    see(
      types: readonly FilterType[] | undefined,
      value: string,
      act: { refetch: () => Promise<TypesRead>; clear: () => void },
    ): Promise<void> | undefined {
      current = value;
      if (deadFilterStep(types, value) === 'keep' || asked.has(value)) return undefined;
      asked.add(value);
      return act.refetch().then(
        (read) => {
          const gone =
            read.status === 'success' && deadFilterStep(read.data, value) === 'confirm';
          if (gone && alive && current === value) act.clear();
        },
        () => undefined, // a failed ask keeps the address
      );
    },
  };
}

export type DeadFilterWatch = ReturnType<typeof deadFilterWatch>;

/**
 * The Jobs list's service type filter (ops insights 08, spec stories 21–22): every job, the
 * jobs with no type (the backlog the office classifies), or one type. Every live type is
 * offered, turned-off ones too and marked so: a turned-off type stays on the jobs that have it,
 * and "every Installation" is still a fair question. The server narrows the list
 * (`jobListInput.serviceType`); this only says which. No hooks.
 */
export function ServiceTypeFilter({
  value,
  types,
  onChange,
}: {
  /** '' for every job, `NO_SERVICE_TYPE`, or a type's id (`shownServiceTypeFilter`). */
  value: string;
  /** The shop's list as `serviceType.list` answers; undefined while it loads. */
  types: readonly FilterType[] | undefined;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-muted-foreground">
      {/* Not "Service type": the set step's picker is on the same page ("Set to"). */}
      Filter by service type
      <select
        className="rounded-[var(--radius-sm)] border border-border bg-surface px-2 py-1.5 text-sm text-[var(--sw-text)]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">All</option>
        <option value={NO_SERVICE_TYPE}>No service type</option>
        {(types ?? []).map((type) => (
          <option key={type.id} value={type.id}>
            {type.active ? type.label : `${type.label} (turned off)`}
          </option>
        ))}
      </select>
    </label>
  );
}
